// Globals
import { useTranslation } from 'react-i18next';

// Modules
import { useIsMobile } from '@/modules/Adaptive';
import { DocumentItem } from '@/modules/SharedDocuments';

// Models
import { IPropertyFile, ISelfDeclaration } from '@/models/documents.model';
import { IFile } from '@/models/file.model';

// Components
import { UploadIcon } from '@/icons';
import { Button } from '@/ui';
import {
  StyledFormBlockWrap,
  StyledSecondaryTitle,
  StyledContainer,
  StyledDragger,
  StyledDraggerDescription,
  StyledDraggerPlaceholder,
  StyledDragIcon,
  StyledFormSection,
  StyledFormSectionRow,
  StyledUploadButton,
  StyledText,
} from './DocumentsManagementStyles';
import SelfDeclarationBlock from '../SelfDeclarationBlock/SelfDeclarationBlock';

// Helpers
import { ACCEPT_PDF_FILE_TYPES, MAX_PDF_SIZE } from '../../helpers/constants';

type TDocumentsManagement = {
  onDocumentDelete: (id: string) => void;
  onFileUpload: (files: File[]) => void;
  filesList?: IPropertyFile[] | IFile[];
  legalDocuments?: IPropertyFile[];
  selfDeclaration?: ISelfDeclaration | null;
  uploadHidden?: boolean;
  disabled?: boolean;
};

const DocumentsManagement = ({
  onFileUpload,
  onDocumentDelete,
  legalDocuments,
  selfDeclaration,
  uploadHidden,
  disabled,
  filesList,
}: TDocumentsManagement) => {
  const { t } = useTranslation('documents');
  const { isMobile } = useIsMobile();

  return (
    <StyledContainer>
      <StyledFormBlockWrap>
        <StyledFormSection>
          <StyledSecondaryTitle>
            {t('documents_property_documents', {
              count: filesList?.length ?? 0,
            })}
          </StyledSecondaryTitle>

          {!uploadHidden && (
            <StyledFormSectionRow>
              <StyledDragger
                acceptance={ACCEPT_PDF_FILE_TYPES}
                imageMaxSize={MAX_PDF_SIZE}
                maxCount={10}
                onChange={onFileUpload}
                showUploadList={false}
                multiple
              >
                {isMobile ? (
                  <StyledUploadButton type={'primary'} shape="round">
                    {t('documents_upload_select_from_phone')}
                    <UploadIcon />
                  </StyledUploadButton>
                ) : (
                  <StyledDraggerPlaceholder>
                    <StyledDraggerDescription>
                      <StyledDragIcon />
                      {t('documents_legal_drag_documents')}
                    </StyledDraggerDescription>

                    <Button type="primary" shape="round">
                      {t('documents_upload_select_documents')}
                    </Button>
                  </StyledDraggerPlaceholder>
                )}
              </StyledDragger>
            </StyledFormSectionRow>
          )}
          {filesList?.map((file) => (
            <DocumentItem key={file.id} document={file} onDocumentDelete={onDocumentDelete} disabled={disabled} />
          ))}
        </StyledFormSection>
        {!!legalDocuments?.length && (
          <>
            <StyledSecondaryTitle>{t('documents_legal_documents', { count: 2 })}</StyledSecondaryTitle>
            <StyledText>{t('documents_legal_documents_description')}</StyledText>
            {legalDocuments?.map((file) => (
              <DocumentItem key={file.file_link} document={file} />
            ))}
          </>
        )}
        {!!selfDeclaration && (
          <>
            <StyledSecondaryTitle>{t('documents_self_declaration')}</StyledSecondaryTitle>
            <SelfDeclarationBlock selfDeclaration={selfDeclaration} />
          </>
        )}
      </StyledFormBlockWrap>
    </StyledContainer>
  );
};

export default DocumentsManagement;
